import {
  getPasskeyList,
  PasskeyItem,
  patchOptOut,
  postAuthenticateBegin,
  postAuthenticateFinish,
  postRegisterBegin,
  postRegisterFinish,
} from '@lib/api/v2/passkey';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import { setToken } from '@util/token';

const logger = createLogger(LOG_PREFIX.AUTH);

/** "나중에" 30일 스킵 localStorage 키 */
export const PASSKEY_PROMPT_SKIP_KEY = 'passkeyPromptSkipUntil';
const SKIP_DAYS = 30;
export const USERNAMELESS_PASSKEY_CUTOFF = new Date('2026-07-03T00:05:59.000Z').getTime();

export const passkeyNeedsUsernamelessReenroll = (passkey: Partial<PasskeyItem>): boolean => {
  if (passkey.usernamelessReady === true) return false;
  if (passkey.usernamelessReady === false) return true;
  if (!passkey.createdAt) return false;
  const createdAt = new Date(passkey.createdAt).getTime();
  return Number.isFinite(createdAt) && createdAt < USERNAMELESS_PASSKEY_CUTOFF;
};

/**
 * 패스키(WebAuthn) 사용 훅. (LETMEUP-403)
 * - 지원 여부/오리진 허용 판단
 * - 인증(로그인) / 등록 / 목록 / opt-out 흐름
 */
const usePasskey = () => {
  /**
   * 패스키 사용 가능 여부.
   * - 브라우저 미지원 시 false
   * - localhost(개발/RP 미설정) 에서는 RP 도메인 불일치로 동작 불가 → 버튼 숨김
   */
  const isPasskeySupported = (): boolean => {
    try {
      if (typeof window === 'undefined') return false;
      if (!browserSupportsWebAuthn()) return false;
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return false;
      return true;
    } catch {
      return false;
    }
  };

  /** "나중에" 30일 스킵이 아직 유효한지 */
  const isPromptSkipped = (): boolean => {
    try {
      const until = localStorage.getItem(PASSKEY_PROMPT_SKIP_KEY);
      if (!until) return false;
      return Date.now() < Number(until);
    } catch {
      return false;
    }
  };

  /** "나중에" → 30일 뒤까지 등록 권유 비노출 */
  const skipPromptFor30Days = (): void => {
    try {
      const until = Date.now() + SKIP_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(PASSKEY_PROMPT_SKIP_KEY, String(until));
    } catch (e) {
      logger.warn('패스키 스킵 키 저장 실패', {
        error: e instanceof Error ? e.message : 'unknown',
      });
    }
  };

  /**
   * 패스키 로그인.
   * @returns loginType('merchant'|'admin') 성공, null 실패/취소
   */
  const authenticate = async (merchantCode: string): Promise<string | null> => {
    try {
      const beginRes = await postAuthenticateBegin(merchantCode);
      const { sessionId, options } = beginRes.data || {};
      if (!sessionId || !options) return null;

      // 사용자 인증기(생체/PIN) 호출 — 취소 시 throw
      // @simplewebauthn/browser v11: 단일 객체 인자 { optionsJSON } 시그니처 (v10 위치 인자에서 breaking change)
      const assertion = await startAuthentication({ optionsJSON: options });

      const finishRes = await postAuthenticateFinish(sessionId, assertion);
      const { accessToken, refreshToken, loginType } = finishRes.data || {};
      if (!accessToken || !refreshToken || !loginType) return null;

      // 기존 merchantLogin 과 동일한 토큰 저장 방식 재사용
      if (loginType === 'admin') {
        setToken('adminToken', accessToken);
        setToken('adminRefreshToken', refreshToken);
        localStorage.setItem('level', 'admin');
      } else {
        setToken('merchantToken', accessToken);
        setToken('merchantRefreshToken', refreshToken);
        localStorage.setItem('level', 'merchant');
      }
      logger.info('패스키 로그인 성공', { loginType });
      return loginType;
    } catch (e) {
      // 사용자 취소(NotAllowedError)는 흔한 정상 흐름 — 조용히 실패 반환
      const name = e instanceof Error ? e.name : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        logger.info('패스키 로그인 취소');
        return null;
      }
      logger.warn('패스키 로그인 실패', {
        error: e instanceof Error ? e.message : 'unknown',
      });
      return null;
    }
  };

  /**
   * 패스키 등록(로그인 상태에서 호출).
   * @returns 성공 여부
   */
  const register = async (deviceName?: string): Promise<boolean> => {
    try {
      const beginRes = await postRegisterBegin();
      const { sessionId, options } = beginRes.data || {};
      if (!sessionId || !options) return false;

      // @simplewebauthn/browser v11: 단일 객체 인자 { optionsJSON } 시그니처 (v10 위치 인자에서 breaking change)
      const attestation = await startRegistration({ optionsJSON: options });
      await postRegisterFinish(sessionId, attestation, deviceName);
      logger.info('패스키 등록 성공');
      return true;
    } catch (e) {
      const name = e instanceof Error ? e.name : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        logger.info('패스키 등록 취소');
        return false;
      }
      logger.warn('패스키 등록 실패', {
        error: e instanceof Error ? e.message : 'unknown',
      });
      throw e;
    }
  };

  /**
   * 등록 권유 페이지 진입 시 평가.
   * @returns shouldPrompt true 면 등록 권유 노출, false 면 즉시 목적지 pass-through.
   *  - 네트워크 실패: 조용히 pass-through(로그인 흐름 차단 금지)
   */
  const evaluatePrompt = async (): Promise<{
    shouldPrompt: boolean;
    promptType: 'none' | 'register' | 'reenroll';
    passkeys: PasskeyItem[];
  }> => {
    // 30일 스킵 키 → 즉시 통과
    if (isPromptSkipped()) return { shouldPrompt: false, promptType: 'none', passkeys: [] };
    try {
      const res = await getPasskeyList();
      const passkeys: PasskeyItem[] = res.data?.passkeys ?? [];
      const optOut: boolean = !!res.data?.optOut;
      const hasLegacyPasskey = passkeys.some(passkeyNeedsUsernamelessReenroll);
      if (hasLegacyPasskey) return { shouldPrompt: true, promptType: 'reenroll', passkeys };
      // opt-out 이거나 이미 등록된 패스키가 있으면 권유하지 않음
      if (optOut || passkeys.length > 0) return { shouldPrompt: false, promptType: 'none', passkeys };
      return { shouldPrompt: true, promptType: 'register', passkeys };
    } catch (e) {
      logger.warn('패스키 목록 조회 실패 — 권유 생략', {
        error: e instanceof Error ? e.message : 'unknown',
      });
      return { shouldPrompt: false, promptType: 'none', passkeys: [] };
    }
  };

  /** "다시 안 보기" → 서버 opt-out */
  const optOut = async (): Promise<void> => {
    try {
      await patchOptOut(true);
      logger.info('패스키 권유 영구 비노출 설정');
    } catch (e) {
      logger.warn('패스키 opt-out 실패', {
        error: e instanceof Error ? e.message : 'unknown',
      });
    }
  };

  return {
    isPasskeySupported,
    isPromptSkipped,
    skipPromptFor30Days,
    authenticate,
    register,
    evaluatePrompt,
    optOut,
  };
};

export default usePasskey;
